import { LoadingOverlayProps } from "@mantine/core";
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useReducer } from "react";

type LoaderContextProps = {
  isLoading: boolean;
  loader: {
    props?: LoadingOverlayProps["loaderProps"];
    overlay?: LoadingOverlayProps["overlayProps"];
    transition?: LoadingOverlayProps["transitionProps"];
  };
  setLoading: (isLoading: boolean, options?: { onLoad?: () => void; onSuccess?: () => void; onError?: () => void }) => void;
  setLoaderProps: (props: LoaderContextProps["loader"]) => void;
};

type ActionProps = { type: "SET_LOADING"; payload?: boolean } | { type: "SET_LOADER_PROPS"; payload: LoaderContextProps["loader"] };
type LoaderState = {
  isLoading: boolean;
  loader: LoaderContextProps["loader"];
};

const initialState: LoaderState = {
  isLoading: false,
  loader: {
    props: {},
    overlay: {},
    transition: {}
  }
};

function reducer(state: LoaderState, action: ActionProps): LoaderState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload ?? false };
    case "SET_LOADER_PROPS":
      return { ...state, loader: { ...state.loader, ...action.payload } };
    default:
      return state;
  }
}

const LoaderContext = createContext({} as LoaderContextProps);

const LoaderProvider = ({ children }: PropsWithChildren<{ loader?: LoaderContextProps["loader"] }>) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setLoading = useCallback(
    (isLoading: boolean, _options?: { onLoad?: () => void; onSuccess?: () => void; onError?: () => void }) => {
      dispatch({ type: "SET_LOADING", payload: isLoading });
    },
    [state.isLoading]
  );

  const setLoaderProps = useCallback(
    (props: LoaderContextProps["loader"]) => {
      dispatch({ type: "SET_LOADER_PROPS", payload: props });
    },
    [state.loader.props]
  );

  const memoizedState = useMemo(() => state, [state.isLoading]);

  return <LoaderContext.Provider value={{ ...memoizedState, setLoading, setLoaderProps }}>{children}</LoaderContext.Provider>;
};

const useLoader = () => {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error("useLoader must be used within a LoaderProvider");
  }

  return context;
};

export { LoaderContext, LoaderProvider, useLoader };
